import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  makeStyles,
  Popover,
  Switch,
  Typography,
} from "@material-ui/core";
import TuneIcon from "@material-ui/icons/Tune";
import { postCustomCTA, setBuilderPermissions } from "adapters";
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  GetUrlBuilderData,
  SetUrlBuilderData,
  UpdateBuilderPermissions,
} from "store/url-builder";
import { ObjectPermissionSetData } from "types/permissions";
import { ChangeConfDialog } from "../changable-confirm-dialog";

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12), 0 0 2px rgba(0, 0, 0, 0.08)",
    minWidth: 240,
    border: "1px solid rgba(0, 0, 0, 0.06)",
  },
  iconButton: {
    padding: 6,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  header: {
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: "#fafafa",
  },
  content: {
    padding: theme.spacing(1.5, 1),
  },
  permissionItem: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 6,
    margin: theme.spacing(0.5, 0),
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
    "& .MuiFormControlLabel-root": {
      marginLeft: 0,
      marginRight: 0,
      width: "100%",
    },
  },
  permissionLabel: {
    textTransform: "capitalize",
    fontSize: "0.875rem",
    color: "#424242",
    fontWeight: 500,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    zIndex: 1,
    borderRadius: 8,
  },
}));

export interface ModuleSettingsProps {
  permissions: ObjectPermissionSetData[];
  builderType: string;
}

export const ModuleSettings = ({
  permissions = [],
  builderType,
}: ModuleSettingsProps) => {
  const classes = useStyles();
  const { cardId }: any = useParams();
  const dispatch = useDispatch();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "ModuleSettings",
  });

  const builderData = useSelector(GetUrlBuilderData);

  const [confirmDialogue, setConfirmDialogue] = useState(false);
  const [permission, setPermission] = useState<ObjectPermissionSetData | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const updatePermission = async (permission: ObjectPermissionSetData) => {
    try {
      setLoading(true);
      const updatedPermissionData = await setBuilderPermissions(
        cardId,
        permission
      );

      if (!updatedPermissionData.data.success) {
        throw new Error(updatedPermissionData.data.message);
      }

      await enablePublish();

      setConfirmDialogue(false);
      setPermission(null);
      dispatch(UpdateBuilderPermissions(updatedPermissionData.data.data));
      toast.success("Permissions updated");
    } catch (err: any) {
      toast.error(err.message || "Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  const enablePublish = async () => {
    try {
      const { data } = await postCustomCTA(
        cardId,
        builderType,
        builderData.customCTA
      );
      if (data.success) {
        dispatch(SetUrlBuilderData(data.data));
      }
    } catch (err) {
      toast.error("Error while enabling publish");
    }
  };

  const shouldShowPermission = (permission: ObjectPermissionSetData) => {
    return !(
      builderType === "PRO" &&
      permission.objectPermission.objectPermissionName === "changeable"
    );
  };

  const isPermissionDisabled = (permission: ObjectPermissionSetData) => {
    const changeablePermission = permissions.find(
      (p) => p.objectPermission.objectPermissionName === "changeable"
    );
    return (
      !changeablePermission?.objectPermissionValue && builderType === "PRO"
    );
  };

  const handlePermissionChange = (permission: ObjectPermissionSetData) => {
    const updatedPermission = {
      ...permission,
      objectPermissionValue: !permission.objectPermissionValue,
    };

    if (
      builderType !== "PRO" &&
      permission.objectPermission.objectPermissionName === "changeable"
    ) {
      setPermission(updatedPermission);
      setConfirmDialogue(true);
    } else {
      updatePermission(updatedPermission);
    }
  };

  const visiblePermissions = permissions.filter(shouldShowPermission);

  return (
    <>
      <IconButton
        {...bindTrigger(popupState)}
        className={classes.iconButton}
        size="small"
      >
        <TuneIcon fontSize="small" />
      </IconButton>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        <Box position="relative">
          {loading && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress size={24} />
            </Box>
          )}

          <Box className={classes.header}>
            <Typography variant="subtitle1" color="inherit">
              <b>Permissions</b>
            </Typography>
          </Box>

          <Box className={classes.content}>
            {visiblePermissions.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No permissions
              </Typography>
            ) : (
              visiblePermissions.map((permission, idx) => (
                <Box key={idx} className={classes.permissionItem}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        disabled={isPermissionDisabled(permission) || loading}
                        checked={permission.objectPermissionValue}
                        onChange={() => handlePermissionChange(permission)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography className={classes.permissionLabel}>
                        {permission.objectPermission.objectPermissionName}
                      </Typography>
                    }
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Popover>

      {confirmDialogue && permission && (
        <ChangeConfDialog
          open={confirmDialogue}
          onClose={() => {
            setConfirmDialogue(false);
            setPermission(null);
          }}
          permission={permission}
          onSubmit={() => {
            updatePermission(permission);
          }}
          loading={loading}
          error
        />
      )}
    </>
  );
};
